"use client";

import React, { useState, useRef, useEffect } from "react";
import { ShareIcon } from "@heroicons/react/24/outline";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { FaXTwitter, FaTelegram } from "react-icons/fa6";
import WhatsappIcon from "@/components/atoms/dynamic-icons/whatsapp";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import "./share-menu.css";

interface ShareMenuProps {
  url: string;
  className?: string;
}

const ShareMenu: React.FC<ShareMenuProps> = ({ url, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [copyTooltipText, setCopyTooltipText] = useState("Copy URL");
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [iconOffsets, setIconOffsets] = useState<{ x: number; y: number }[]>(
    Array.from({ length: 4 }, () => ({ x: 0, y: 0 }))
  );
  const [hoveredIcon, setHoveredIcon] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Click outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
        setIconOffsets(Array.from({ length: 4 }, () => ({ x: 0, y: 0 })));
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleClick = () => {
    setOpen((prev) => !prev);
    if (open) {
      setIconOffsets(Array.from({ length: 4 }, () => ({ x: 0, y: 0 })));
    }
  };

  const getIconTransform = (index: number) => {
    if (!open) return "translate(-50%, -50%)";

    const offset = iconOffsets[index] || { x: 0, y: 0 };
    const isHovered = hoveredIcon === index;
    const scale = isHovered ? 1.1 : 1;

    // Semicircle configuration
    const radius = 60; // Distance from center in pixels
    const startAngle = 250; // Start angle in degrees (left side)
    const endAngle = 360; // End angle in degrees (right side)
    const totalAngle = endAngle - startAngle;
    const angleStep = totalAngle / 3; // Divide semicircle into 3 equal parts for 4 icons

    // Calculate angle for each icon (distributed evenly across semicircle)
    const angle = startAngle + angleStep * index;
    const angleRad = (angle * Math.PI) / 180;

    // Calculate x and y positions using trigonometry
    const x = radius * Math.cos(angleRad);
    const y = radius * Math.sin(angleRad);

    // Rotation based on position in semicircle (icons face outward)
    const rotation = angle + 90; // Add 90 to make icons face outward

    // const transforms: Record<number, string> = {
    //   0: `translate(calc(-50% - 120% + ${offset.x}px), calc(-50% - 70% + ${offset.y}px)) rotateZ(-15deg) scale(${scale})`,
    //   1: `translate(calc(-50% + 10% + ${offset.x}px), calc(-50% - 70% + ${offset.y}px)) rotateZ(15deg) scale(${scale})`,
    //   2: `translate(calc(-50% + ${offset.x}px), calc(-50% - 100% + ${offset.y}px)) rotateZ(5deg) scale(${scale})`,
    //   3: `translate(calc(-50% + ${offset.x}px), calc(-50% + 30% + ${offset.y}px)) rotateZ(-10deg) scale(${scale})`,
    // };

    return `translate(calc(-50% + ${x}px + ${offset.x}px), calc(-50% + ${y}px + ${offset.y}px)) rotateZ(${rotation}deg) scale(${scale})`;
  };

  const handleIconMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    if (!open) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const offsetX = (e.clientX - centerX) * 0.15;
    const offsetY = (e.clientY - centerY) * 0.15;

    setIconOffsets((prev) => {
      const newOffsets = [...prev];
      newOffsets[index] = { x: offsetX, y: offsetY };
      return newOffsets;
    });
  };

  const handleIconMouseLeave = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    setIconOffsets((prev) => {
      const newOffsets = [...prev];
      newOffsets[index] = { x: 0, y: 0 };
      return newOffsets;
    });
    setHoveredIcon(null);
  };

  const handleIconMouseEnter = (index: number) => {
    setHoveredIcon(index);
  };

  const handleShare = (platform: "whatsapp" | "twitter" | "telegram") => {
    const encodedURL = encodeURIComponent(url);
    let shareUrl = "";

    switch (platform) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodedURL}`;
        break;
      case "twitter":
        shareUrl = `https://x.com/intent/tweet?url=${encodedURL}`;
        break;
      case "telegram":
        shareUrl = `https://t.me/share/url?url=${encodedURL}`;
        break;
    }

    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyTooltipText("Copied");
      setCopyTooltipOpen(true);

      // Clear any existing timeout
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      // Keep tooltip open for 4 seconds, then reset
      copyTimeoutRef.current = setTimeout(() => {
        setCopyTooltipText("Copy URL");
        setCopyTooltipOpen(false);
      }, 4000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const shareMenuClassName = `share-menu ${open ? "open" : ""}`.trim();

  return (
    <div ref={menuRef} className={`share-menu-wrapper ${className}`}>
      <div className={shareMenuClassName} onClick={handleClick}>
        <div className='share-menu__button'>
          <ShareIcon className='size-5 stroke-[1.8px] text-[#364153]' />
        </div>

        {/* WhatsApp Icon */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`share-icon share-icon-1 flex size-7 items-center justify-center bg-white shadow-lg ${open ? "open" : ""}`}
              onMouseEnter={() => handleIconMouseEnter(0)}
              onMouseMove={(e) => handleIconMouseMove(e, 0)}
              onMouseLeave={(e) => handleIconMouseLeave(e, 0)}
              onClick={(e) => {
                e.stopPropagation();
                handleShare("whatsapp");
              }}
              style={{
                transform: getIconTransform(0),
              }}
            >
              <WhatsappIcon className='size-4 text-[#364153]' />
            </div>
          </TooltipTrigger>
          <TooltipContent>Share to WhatsApp</TooltipContent>
        </Tooltip>

        {/* Twitter Icon */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`share-icon share-icon-2 flex size-7 items-center justify-center bg-white shadow-lg ${open ? "open" : ""}`}
              onMouseEnter={() => handleIconMouseEnter(1)}
              onMouseMove={(e) => handleIconMouseMove(e, 1)}
              onMouseLeave={(e) => handleIconMouseLeave(e, 1)}
              onClick={(e) => {
                e.stopPropagation();
                handleShare("twitter");
              }}
              style={{
                transform: getIconTransform(1),
              }}
            >
              <FaXTwitter className='size-4 text-[#364153]' />
            </div>
          </TooltipTrigger>
          <TooltipContent>Share to Twitter</TooltipContent>
        </Tooltip>

        {/* Telegram Icon */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`share-icon share-icon-3 flex size-7 items-center justify-center bg-white shadow-lg ${open ? "open" : ""}`}
              onMouseEnter={() => handleIconMouseEnter(2)}
              onMouseMove={(e) => handleIconMouseMove(e, 2)}
              onMouseLeave={(e) => handleIconMouseLeave(e, 2)}
              onClick={(e) => {
                e.stopPropagation();
                handleShare("telegram");
              }}
              style={{
                transform: getIconTransform(2),
              }}
            >
              <FaTelegram className='size-4 text-[#364153]' />
            </div>
          </TooltipTrigger>
          <TooltipContent>Share to Telegram</TooltipContent>
        </Tooltip>

        {/* Copy URL Icon */}
        <Tooltip
          open={copyTooltipOpen || undefined}
          onOpenChange={(open) => {
            // Only allow closing if we're not showing "Copied"
            if (copyTooltipText !== "Copied") {
              setCopyTooltipOpen(open);
            }
          }}
        >
          <TooltipTrigger asChild>
            <div
              className={`share-icon share-icon-4 flex size-7 items-center justify-center bg-white shadow-lg ${open ? "open" : ""}`}
              onMouseEnter={() => {
                handleIconMouseEnter(3);
                if (copyTooltipText !== "Copied") {
                  setCopyTooltipOpen(true);
                }
              }}
              onMouseMove={(e) => handleIconMouseMove(e, 3)}
              onMouseLeave={(e) => {
                handleIconMouseLeave(e, 3);
                if (copyTooltipText !== "Copied") {
                  setCopyTooltipOpen(false);
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCopyUrl();
              }}
              style={{
                transform: getIconTransform(3),
              }}
            >
              <ClipboardDocumentIcon className='size-4 stroke-[1.8px] text-[#364153]' />
            </div>
          </TooltipTrigger>
          <TooltipContent>{copyTooltipText}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

export default ShareMenu;
